import React from 'react'
import { Grid, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import Card from 'components/Shared/Card'
import KeyMetricsSummary from 'components/Reporting/Overview/GraphicalDetails/KeyMetricsSummary'
import ActivityOverTime from 'components/Reporting/Overview/GraphicalDetails/ActivityOverTime'
import VisitorEngagement from 'components/Reporting/Overview/GraphicalDetails/VisitorEngagement'
import TopPerformingTools from 'components/Reporting/Overview/GraphicalDetails/TopPerformingTools'
import ContributionTags from 'components/Reporting/Overview/GraphicalDetails/ContributionTags'
import ContributionsLocation from 'components/Reporting/Overview/GraphicalDetails/ContributionsLocation'
import type { KeyMetric as KeyMetricType } from 'types/KeyMetric'
import { activityOverTime as activityOverTimeType } from 'types/ActivityOverTime'
import { VisitorEngagement as VisitorEngagementType } from 'types/VisitorEngagement'
import { TopPerformingTools as TopPerformingToolsType } from 'types/TopPerformingTools'
import { ContributionTags as ContributionTagsType } from 'types/ContributionTags'
import { ContributionsLocation as ContributionsLocationType } from 'types/ContributionsLocation'
import { SelectDropdown as SelectDropdownType } from 'types/SelectDropdown'

type Props = {
  isLoading: {
    isKeyMetricsLoading: boolean
    isActivityLoading: boolean
    isVisitorEngagementLoading: boolean
    isTopPerformingToolsLoading: boolean
    isContributionTagsLoading: boolean
    isContributionsLocationLoading: boolean
  }
  keyMetrics: KeyMetricType[]
  activityData: activityOverTimeType
  visitorEngagementData: VisitorEngagementType
  topPerformingToolsData: TopPerformingToolsType
  contributionTagsData: ContributionTagsType
  contributionsLocationData: ContributionsLocationType
  onSetActivityFilterDropdown: (interval: string) => void
  activityOverTimeOptions: SelectDropdownType
  activityOverTimeSelection: { period: string }
}

const GraphicalDetails = (props: Props) => {
  const {
    keyMetrics,
    activityData,
    visitorEngagementData,
    topPerformingToolsData,
    contributionTagsData,
    contributionsLocationData,
    isLoading,
    onSetActivityFilterDropdown,
    activityOverTimeOptions,
    activityOverTimeSelection,
  } = props

  const {
    isKeyMetricsLoading,
    isActivityLoading,
    isVisitorEngagementLoading,
    isTopPerformingToolsLoading,
    isContributionTagsLoading,
    isContributionsLocationLoading,
  } = isLoading

  const { t } = useTranslation()

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={3}>
        <KeyMetricsSummary
          keyMetrics={keyMetrics}
          isLoading={isKeyMetricsLoading}
        />
      </Grid>
      <Grid item xs={12} md={9}>
        <ActivityOverTime
          activityOverTimeData={activityData}
          isLoading={isActivityLoading}
          onSetActivityFilterDropdown={onSetActivityFilterDropdown}
          activityOverTimeOptions={activityOverTimeOptions}
          activityOverTimeSelection={activityOverTimeSelection}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <VisitorEngagement
          visitorEngagementData={visitorEngagementData}
          isLoading={isVisitorEngagementLoading}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={6} lg={4} xl={4} display={'none'}>
        <Card
          header={t('reporting.overview.overallSentiments')}
          subheader={t('reporting.overview.subHeader')}
          content={
            <Typography>{t('reporting.overview.overallSentiments')}</Typography>
          }
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <ContributionTags
          contributionTagsData={contributionTagsData}
          isLoading={isContributionTagsLoading}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
        <ContributionsLocation
          isLoading={isContributionsLocationLoading}
          contributionsLocationData={contributionsLocationData}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
        <TopPerformingTools
          isLoading={isTopPerformingToolsLoading}
          topPerformingToolsData={topPerformingToolsData}
        />
      </Grid>
    </Grid>
  )
}

export default GraphicalDetails
